import React, { useEffect, useMemo, useState } from 'react';
import Navbar from '../components/navbar';
import Footer from '../components/footer';
import { Helmet } from '@dr.pogodin/react-helmet';

import { doc, getDoc, } from 'firebase/firestore';
import { db } from '../firebaseConfig';
import { candleShippingCents, normalizeState } from '../candleShipping';

const PICKUP_PRICE = 15; // USD per candle
const DELIVERY_PRICE = 17; // USD per candle
const DEFAULT_CANDLE_INVENTORY_STATUS = 'In Stock'; // 'In Stock' | 'Out of Stock' | 'Pre Order'

const CandlePreOrderPage = () => {
    const [quantity, setQuantity] = useState(1);
    const [qtyInput, setQtyInput] = useState('1');
    const [fulfillment, setFulfillment] = useState('pickup'); // 'delivery' | 'pickup' — pickup is the default (pre-orders switch to delivery below)
    const [isSubmitting, setIsSubmitting] = useState(false);
    const [inventoryStatus, setInventoryStatus] = useState(DEFAULT_CANDLE_INVENTORY_STATUS);

    const isOutOfStock = inventoryStatus === 'Out of Stock';
    const isPreOrder = inventoryStatus === 'Pre Order';

    const [addr, setAddr] = useState({
        name: '',
        email: '',
        phone: '',
        line1: '',
        line2: '',
        city: '',
        state: '',
        postalCode: ''
    });
    const _state = normalizeState(addr.state);
    const shippingRegionLabel = _state === '' ? 'est.' : _state;
    const onAddr = (e) => setAddr(a => ({ ...a, [e.target.name]: e.target.value }));

    useEffect(() => {
        const fetchInventoryStatus = async () => {
            try {
                const ref = doc(db, 'inventory', 'Candle');
                const snap = await getDoc(ref);
                if (snap.exists()) {
                    const data = snap.data() || {};
                    if (data.status && typeof data.status === 'string') {
                        setInventoryStatus(data.status);
                    }
                }
            } catch (err) {
                console.error('Error loading candle inventory status', err);
            }
        };

        fetchInventoryStatus();
    }, []);
    useEffect(() => {
        if (isPreOrder && fulfillment === 'pickup') {
            setFulfillment('delivery');
        }
    }, [isPreOrder, fulfillment]);
    const unitPrice = fulfillment === 'pickup' ? PICKUP_PRICE : DELIVERY_PRICE;
    const itemsSubtotal = useMemo(() => unitPrice * quantity, [unitPrice, quantity]);
    const shippingEstimate = useMemo(() => {
        if (fulfillment !== 'delivery') return 0;
        return candleShippingCents({ quantity, state: addr.state, postalCode: addr.postalCode }) / 100;
    }, [fulfillment, quantity, addr.state, addr.postalCode]);
    const orderTotal = useMemo(() => itemsSubtotal + shippingEstimate, [itemsSubtotal, shippingEstimate]);

    const updateQuantity = (val) => {
        // Allow the field to be empty while typing
        if (val === '') {
            setQtyInput('');
            return;
        }

        // Ignore non-numeric input
        const num = Number(val);
        if (Number.isNaN(num)) return;

        const clamped = Math.max(1, Math.min(10, num));
        setQuantity(clamped);
        setQtyInput(String(clamped));
    };

    const handleQtyBlur = () => {
        // If the user leaves the field empty, reset to 1
        if (qtyInput === '') {
            setQuantity(1);
            setQtyInput('1');
        }
    };

    const requireDeliveryFields = () => {
        if (fulfillment !== 'delivery') return true;
        return (
            addr.name.trim() && addr.email.trim() && addr.line1.trim() && addr.city.trim() && addr.state.trim() && addr.postalCode.trim()
        );
    };

    const handlePreorder = async () => {
        if (!requireDeliveryFields()) {
            alert('Please complete the required delivery fields.');
            return;
        }

        try {
            setIsSubmitting(true);
            const payload = {
                isCandle: true,
                quantity,
                fulfillmentMethod: fulfillment, // 'delivery' or 'pickup'
                shippingAddress: addr,
                returnUrl: window.location.origin + '/candle'
            };

            const response = await fetch(
                'https://us-central1-sole-15b6f.cloudfunctions.net/createStripeSession',
                {
                    method: 'POST',
                    headers: {
                        'Content-Type': 'application/json',
                    },
                    body: JSON.stringify(payload),
                }
            );

            if (!response.ok) {
                const text = await response.text();
                console.error('Server error:', text);
                alert('Checkout failed. Please try again later.');
                setIsSubmitting(false);
                return;
            }

            const data = await response.json();
            if (data.url) {
                window.location.href = data.url;
            } else {
                console.error('Unexpected response:', data);
                alert('Could not start checkout.');
                setIsSubmitting(false);
            }
        } catch (error) {
            console.error('Failed to start checkout:', error);
            alert('An error occurred. Please try again later.');
            setIsSubmitting(false);
        }
    };

    return (
        <div className="donate-css">
            <Helmet>
                <title>SOLE Altar Flame Candle</title>
            </Helmet>
            <Navbar />

            <div className="candle-holder">
                <div className="purchase-container">
                    {/* Product Panel */}
                    <section className="product-card">
                        <div className="product-body">
                            <img
                                src="candledemo.jpg"
                                alt="Altar Flame Candle"
                                className="candle-image-large"
                                loading="lazy"
                            />
                            <h1 className="candle-title">Altar Flame</h1>
                            <p className="candle-subtitle">
                                {isOutOfStock
                                    ? 'Currently out of stock'
                                    : (isPreOrder ? 'Pre‑order now' : 'Now available')}
                            </p>
                            <p className="candle-price">${fulfillment === 'pickup' ? PICKUP_PRICE : DELIVERY_PRICE}.00</p>
                            <p className="candle-description">
                                A sacred fragrance inspired by the beauty of the Mass: <strong>Altar Flame</strong> captures
                                the scent of incense and prayer. Hand‑poured with frankincense, myrrh, rose, and cedarwood blanc to bring
                                the mystery of the sanctuary into your home.
                            </p>

                            <div className="quantity-row" aria-label="Quantity selector">
                                <label htmlFor="qty" className="field-label">Quantity</label>
                                <input
                                    id="qty"
                                    type="number"
                                    min="1"
                                    max="10"
                                    value={qtyInput}
                                    onChange={(e) => updateQuantity(e.target.value)}
                                    onBlur={handleQtyBlur}
                                    className="input qty-input"
                                />
                            </div>
                        </div>
                    </section>

                    {/* Fulfillment + Address */}
                    <section className="checkout-card">
                        <h2 className="section-title">How would you like to receive it?</h2>

                        <div className="fulfillment-options">
                            {!isPreOrder && (
                                <label className={`option ${fulfillment === 'pickup' ? 'selected' : ''}`}>
                                    <input
                                        type="radio"
                                        name="fulfillment"
                                        value="pickup"
                                        checked={fulfillment === 'pickup'}
                                        onChange={() => setFulfillment('pickup')}
                                    />
                                    <div className="option-content">
                                        <div className="option-title">In-person Pickup</div>
                                        <div className="option-note">
                                            Pick up at the SOLE Meeting.
                                        </div>
                                    </div>
                                </label>
                            )}

                            <label className={`option ${fulfillment === 'delivery' ? 'selected' : ''}`}>
                                <input
                                    type="radio"
                                    name="fulfillment"
                                    value="delivery"
                                    checked={fulfillment === 'delivery'}
                                    onChange={() => setFulfillment('delivery')}
                                />
                                <div className="option-content">
                                    <div className="option-title">Delivery</div>
                                    <div className="option-note">Enter your shipping details at checkout.</div>
                                </div>
                            </label>
                        </div>

                        {fulfillment === 'delivery' && (
                            <div className="address-form" style={{ marginTop: '.25rem' }}>
                                <div className="form-row">
                                    <div className="field">
                                        <label className="field-label" htmlFor="name">Full name<span className="req">*</span></label>
                                        <input id="name" name="name" className="input" value={addr.name} onChange={onAddr} />
                                    </div>
                                    <div className="field">
                                        <label className="field-label" htmlFor="email">Email<span className="req">*</span></label>
                                        <input id="email" name="email" className="input" value={addr.email} onChange={onAddr} />
                                    </div>
                                </div>
                                <div className="form-row">
                                    <div className="field">
                                        <label className="field-label" htmlFor="phone">Phone</label>
                                        <input id="phone" name="phone" className="input" value={addr.phone} onChange={onAddr} />
                                    </div>
                                </div>
                                <div className="form-row">
                                    <div className="field">
                                        <label className="field-label" htmlFor="line1">Address line 1<span className="req">*</span></label>
                                        <input id="line1" name="line1" className="input" value={addr.line1} onChange={onAddr} />
                                    </div>
                                    <div className="field">
                                        <label className="field-label" htmlFor="line2">Address line 2</label>
                                        <input id="line2" name="line2" className="input" value={addr.line2} onChange={onAddr} />
                                    </div>
                                </div>
                                <div className="form-row">
                                    <div className="field">
                                        <label className="field-label" htmlFor="city">City<span className="req">*</span></label>
                                        <input id="city" name="city" className="input" value={addr.city} onChange={onAddr} />
                                    </div>
                                    <div className="field">
                                        <label className="field-label" htmlFor="state">State<span className="req">*</span></label>
                                        <input id="state" name="state" className="input" value={addr.state} onChange={onAddr} placeholder="TX" />
                                    </div>
                                    <div className="field">
                                        <label className="field-label" htmlFor="postalCode">ZIP<span className="req">*</span></label>
                                        <input id="postalCode" name="postalCode" className="input" value={addr.postalCode} onChange={onAddr} />
                                    </div>
                                </div>
                            </div>
                        )}

                        {fulfillment === 'delivery' && (
                            <p className="summary-row muted">Shipping is estimated from your state and ZIP (USPS Ground Advantage) and finalized at checkout.</p>
                        )}

                        {/* Order summary */}
                        <div className="summary">
                            <div className="summary-row">
                                <span>{fulfillment === 'pickup' ? 'Candle' : 'Candle (delivery)'} x {quantity}</span>
                                <span>${itemsSubtotal.toFixed(2)}</span>
                            </div>
                            {fulfillment === 'delivery' && (
                                <div className="summary-row">
                                    <span>Shipping ({shippingRegionLabel})</span>
                                    <span>${shippingEstimate.toFixed(2)}</span>
                                </div>
                            )}
                            <div className="summary-row" style={{ fontWeight: 600 }}>
                                <span>Total</span>
                                <span>${orderTotal.toFixed(2)}</span>
                            </div>
                        </div>

                        {isOutOfStock && (
                            <p className="pickup-warning">
                                <strong>Temporarily unavailable:</strong> This candle is currently out of stock. Ordering is
                                disabled until we restock. Please check back soon.
                            </p>
                        )}

                        <button
                            onClick={handlePreorder}
                            className="candle-button checkout-button"
                            disabled={isSubmitting || isOutOfStock || (fulfillment === 'delivery' && !requireDeliveryFields())}
                        >
                            {isOutOfStock
                                ? 'Out of stock'
                                : (isSubmitting
                                    ? 'Starting checkout…'
                                    : `${isPreOrder ? 'Pre‑order' : 'Checkout'} — $${orderTotal.toFixed(2)}`)}
                        </button>

                        {fulfillment === 'pickup' && !isPreOrder && (
                            <p className="pickup-warning">
                                <strong>Note:</strong> In-person pickup is available only at SOLE meetings on Monday nights. Choose <em>Delivery</em> if you would like your order shipped.
                            </p>
                        )}

                        {isPreOrder && !isOutOfStock && (
                            <p className="pickup-warning">
                                <strong>Pre‑order notice:</strong> This candle is currently on backorder. Your order will be reserved
                                and will ship once inventory is restocked. Please allow additional time for fulfillment and shipping.
                            </p>
                        )}
                    </section>
                </div>
            </div>

            <Footer />
        </div>
    );
};

export default CandlePreOrderPage;
